/* Turning MEXC's answer into the shapes this book keeps.
 *
 * Pure, and separate from the fetching in `lib/mexc.ts`, so every rule below is
 * testable against a recorded response rather than against the exchange.
 *
 * Three things this deliberately does not do, each of them a bug that would
 * have been easy to write:
 *
 *   1. It never posts margin. `openPerp` debits the futures wallet when a
 *      contract is opened, because in the book opening one moves money out of
 *      free cash. Here the money moved days ago and the exchange is reporting
 *      the state afterwards: its `availableBalance` is already net of margin.
 *      Writing a position through `openPerp` and the balance beside it would
 *      count the same margin twice — the exact fault §5 was fixed for. What
 *      arrives is written as a record, not replayed as an event.
 *
 *   2. It never closes anything. A contract in the book that MEXC no longer
 *      reports is listed and left alone, because closing writes a realised
 *      result and a cash credit, and the result is the fill the user got — not
 *      something to be inferred from a position's absence.
 *
 *   3. It never overwrites the book's dated record. `closes` carry the day each
 *      realisation happened; MEXC's `realised` is one running total with no
 *      dates in it. On a contract the book already knows, the dated record
 *      stands and a disagreement is reported rather than resolved.
 *
 * On `sizeUsdt`: it is the notional at entry, and §5.1 is only exact because it
 * is. MEXC reports two margins — the one posted at entry and the one posted
 * now, which is larger wherever auto-addition has topped a position up. The
 * notional follows the first; the margin figure follows the second, because
 * that is what is actually locked. Where they differ the row says so, since
 * that is the one case where the two cannot both be margin x leverage. */

import type { ExchangeReading } from './exchangeOutflow';
import type { Perp } from '../types';

export interface MexcWallet {
  currency: string;
  /** Free to use — what the Cash card holds for this account. */
  free: number;
  /** Locked behind open positions. Reported for the comparison, never stored:
      the book derives posted margin from the contracts themselves. */
  margin: number;
  equity: number;
  unrealized: number;
}

export interface MexcChange {
  /** What would be written. */
  perp: Perp;
  /** What the book holds now, when it holds anything for this contract. */
  before: Perp | null;
  /** Something true about this row that the numbers do not say by themselves. */
  notes: string[];
}

export interface MexcSnapshot {
  wallet: MexcWallet | null;
  changes: MexcChange[];
  /** Open in the book, absent from the exchange's answer. Never touched. */
  notAtExchange: Perp[];
  skipped: Array<{ symbol: string; why: string }>;
}

function num(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/* The display name, from the contract symbol.
 *
 * MEXC's equity contracts carry a STOCK suffix on the API symbol that its own
 * position card does not print: MU_USDT on screen, MUSTOCK_USDT on the wire.
 * The symbol stored is always the one the API answers to (§3.2) and the name
 * shown is always the short one, so this strips rather than the reverse. */
export function displayName(symbol: string): string {
  const base = symbol.split('_')[0] ?? symbol;
  const stripped = base.replace(/STOCK$/, '');
  return stripped === '' ? base : stripped;
}

/** The futures wallet in one currency. USDT unless asked otherwise — every
    contract in this book is quoted in it. */
export function readWallet(rows: unknown[], currency = 'USDT'): MexcWallet | null {
  for (const row of rows) {
    const r = row as Record<string, unknown>;
    if (String(r['currency'] ?? '').toUpperCase() !== currency) continue;
    const free = num(r['availableBalance']);
    if (free === null) return null;
    return {
      currency,
      free,
      margin: num(r['positionMargin']) ?? 0,
      equity: num(r['equity']) ?? 0,
      unrealized: num(r['unrealized']) ?? 0,
    };
  }
  return null;
}

/* One open position, as the book would hold it.
 *
 * Everything the exchange knows is taken from the exchange. Everything only the
 * book knows — the take-profit written down, the dated closes, the id other
 * records point at — is carried over from what is already there. A pull is a
 * correction of the market facts, not a replacement of the position. */
function toPerp(row: Record<string, unknown>, before: Perp | null, now: string): MexcChange | { why: string } {
  const symbol = typeof row['symbol'] === 'string' ? row['symbol'] : '';
  if (symbol === '') return { why: 'the row carried no contract symbol' };

  const positionType = num(row['positionType']);
  if (positionType !== 1 && positionType !== 2) {
    return { why: 'the row did not say whether it was long or short' };
  }

  const leverage = num(row['leverage']);
  const im = num(row['im']);
  const entryPrice = num(row['holdAvgPrice']) ?? num(row['openAvgPrice']);
  if (leverage === null || leverage <= 0) return { why: 'no leverage on the row' };
  if (im === null || im <= 0) return { why: 'no margin on the row' };
  if (entryPrice === null || entryPrice <= 0) return { why: 'no entry price on the row' };

  /* The margin posted at entry, where the exchange reports it apart from the
     margin posted now. The notional follows this one — see the header. */
  const oim = num(row['oim']);
  const entryMargin = oim !== null && oim > 0 ? oim : im;

  const notes: string[] = [];
  if (Math.abs(entryMargin - im) > 0.01) {
    notes.push(
      `Margin posted now ${im.toFixed(2)} USDT, ${entryMargin.toFixed(2)} at entry. The size is kept `
      + 'at the entry figure, so the open profit is measured correctly.',
    );
  }

  const openedAtMs = num(row['createTime']);
  const perp: Perp = {
    id: before?.id ?? `mexc-${symbol.toLowerCase()}`,
    symbol,
    display: before?.display ?? displayName(symbol),
    side: positionType === 1 ? 'long' : 'short',
    leverage,
    sizeUsdt: entryMargin * leverage,
    marginUsdt: im,
    entryPrice,
    liqPrice: num(row['liquidatePrice']) ?? before?.liqPrice ?? 0,
    autoMarginAddition: row['autoAddIm'] === true,
  };
  /* The exchange's own liquidation price replaces an estimate; a row without one
     leaves the estimate standing, and still marked as one. */
  if (num(row['liquidatePrice']) === null && before?.liqEstimated) perp.liqEstimated = true;

  /* Only ever set, never cleared: the exchange not reporting an open time is
     not evidence that one was never recorded. */
  const openedAt = before?.openedAt
    ?? (openedAtMs !== null && openedAtMs > 0 ? new Date(openedAtMs).toISOString() : undefined);
  if (openedAt) perp.openedAt = openedAt;

  if (before?.takeProfit !== undefined) perp.takeProfit = before.takeProfit;
  if (before?.closes) perp.closes = before.closes;

  /* The realised total. On a contract the book already knows, the book's figure
     stands — it is the one the dated closes add up to. On a new one there is
     nothing to preserve, so the exchange's total is taken, undated, exactly as
     the hand-entered contracts in this book carry it. */
  const exchangeRealised = num(row['realised']);
  /* Kept either way. The book's own total stands on a contract it knows, but the
     exchange's, which also holds funding and fees, is what shows what those came
     to — and was being compared and discarded on every read. */
  if (exchangeRealised !== null) perp.exchangeRealisedUsdt = exchangeRealised;
  if (before) {
    if (before.realizedUsdt !== undefined) perp.realizedUsdt = before.realizedUsdt;
    if (
      exchangeRealised !== null
      && Math.abs(exchangeRealised - (before.realizedUsdt ?? 0)) > 0.01
    ) {
      const gap = exchangeRealised - (before.realizedUsdt ?? 0);
      notes.push(
        `Realised on MEXC ${exchangeRealised.toFixed(2)} USDT; in the closes recorded here `
        + `${(before.realizedUsdt ?? 0).toFixed(2)}. The ${gap >= 0 ? '+' : ''}${gap.toFixed(2)} between them `
        + 'is funding and fees, shown on the contract.',
      );
    }
  } else if (exchangeRealised !== null && exchangeRealised !== 0) {
    perp.realizedUsdt = exchangeRealised;
    notes.push('Realised total taken from MEXC. It carries no dates, so it does not appear on a day in Results.');
  }

  if (!before) {
    notes.push(`New to the book, recorded ${now.slice(0, 10)}. No thesis or take profit yet.`);
  }

  return { perp, before, notes };
}

/** Everything the exchange said, lined up against everything the book holds. */
export function reconcile(
  assets: unknown[],
  positions: unknown[],
  bookPerps: Perp[],
  now: string,
): MexcSnapshot {
  const open = bookPerps.filter((p) => !p.closedAt);
  const changes: MexcChange[] = [];
  const skipped: MexcSnapshot['skipped'] = [];
  const seen = new Set<string>();

  for (const row of positions) {
    const r = row as Record<string, unknown>;

    /* State 3 is closed and holding nothing. It arrives in the same list and is
       not a position; it is the record of one that has gone. */
    const holdVol = num(r['holdVol']);
    if (holdVol !== null && holdVol <= 0) continue;
    if (num(r['state']) === 3) continue;

    const symbol = typeof r['symbol'] === 'string' ? r['symbol'] : '';

    /* Marked seen before it is read, not after. A row the exchange sent but this
       could not parse is still evidence that the contract is open — counting it
       as absent would put "close this, it is gone from MEXC" next to a position
       that is sitting there. Rows filtered out above are a different thing:
       those the exchange reports as closed, and those belong in the gone list. */
    if (symbol !== '') seen.add(symbol);

    const before = open.find((p) => p.symbol === symbol) ?? null;
    const result = toPerp(r, before, now);
    if ('why' in result) {
      skipped.push({ symbol: symbol || '(no symbol)', why: result.why });
      continue;
    }
    changes.push(result);
  }

  return {
    wallet: readWallet(assets),
    changes,
    notAtExchange: open.filter((p) => !seen.has(p.symbol)),
    skipped,
  };
}

/** Whether a pull would actually change this row, so a preview can say "same as
    the book" instead of offering a write that does nothing. */
export function isUnchanged(change: MexcChange): boolean {
  const b = change.before;
  if (!b) return false;
  const same = (x: number, y: number) => Math.abs(x - y) < 0.005;
  return b.side === change.perp.side
    && same(b.leverage, change.perp.leverage)
    && same(b.marginUsdt, change.perp.marginUsdt)
    && same(b.sizeUsdt, change.perp.sizeUsdt)
    && same(b.entryPrice, change.perp.entryPrice)
    && same(b.liqPrice, change.perp.liqPrice);
}


/* What may be written without anybody looking at it first.
 *
 * The manual pull shows every difference and waits for a press, which is right
 * for the first time a key is used and wrong as a way to keep a book current —
 * a balance that is only correct when you remember to press something is a
 * balance that is usually wrong.
 *
 * So the automatic path writes the things the exchange is simply the authority
 * on: what is posted as margin, at what leverage, from what entry, and how much
 * is free. Those are not opinions the book holds and MEXC contradicts; they are
 * facts about an account this book does not own.
 *
 * It still never closes anything. A contract the exchange has stopped reporting
 * is handed back to be dealt with, because closing writes a realised result and
 * that result is the fill the user got — not something to infer from an absence.
 * And it still never posts margin: these are records of a state that already
 * happened, so `putPerp`, never `openPerp`. */
export interface AutoPlan {
  /** Contracts to write: the ones that changed, and the ones that are new. */
  perps: Perp[];
  /** The free balance, when it differs from what the book holds. */
  cash: number | null;
  /** Open here, absent there. Never written — only reported. */
  goneFromExchange: Perp[];
  /** True when there is nothing at all to do. */
  quiet: boolean;
  /** What the exchange is holding right now, free and posted as margin, so a
      caller can tell a withdrawal from money moved into a contract. Reported
      rather than written — see `state/exchangeOutflow.ts`. */
  reading: ExchangeReading | null;
}

export function autoPlan(snap: MexcSnapshot, heldCash: number | undefined): AutoPlan {
  const perps = snap.changes.filter((c) => !isUnchanged(c)).map((c) => c.perp);
  const cash = snap.wallet !== null && Math.abs((heldCash ?? 0) - snap.wallet.free) >= 0.0001
    ? snap.wallet.free
    : null;
  /* Every contract the exchange is reporting, not only the ones that changed:
     the margin total has to be the whole of what is posted, or money moved into
     an untouched position would read as money that left the exchange. */
  const margin = snap.changes.reduce((sum, c) => sum + (c.perp.marginUsdt || 0), 0);

  return {
    perps,
    cash,
    goneFromExchange: snap.notAtExchange,
    quiet: perps.length === 0 && cash === null,
    reading: snap.wallet === null ? null : { free: snap.wallet.free, margin },
  };
}
